import { Link, TableProps } from "@amzn/awsui-components-react";
import { OptionDefinition } from "@amzn/awsui-components-core/internal/components/option/interfaces";
import { Stage, Transaction } from "../lusca-search-api/generated-src";
import { GL_MAPPING } from "../hooks/useSummaryData";
import { PERMISSION_LINK } from "../constants";
import { FLOW_PARAM } from "../hooks/useFlowType";

export const TIME_RANGES: readonly OptionDefinition[] = [
  {
    label: "Custom date",
    value: "custom",
  },
  {
    label: "5 mins ago",
    value: "5min",
  },
  {
    label: "15 mins ago",
    value: "15min",
  },
  {
    label: "1 hour ago",
    value: "1hour",
  },
  {
    label: "4 hours ago",
    value: "4hours",
  },
  {
    label: "1 day ago",
    value: "24hours",
  },
  {
    label: "Last week",
    value: "1week",
  },
  {
    label: "Last month",
    value: "1month",
  },
] as const;

export const COLUMN_DEFINITIONS: TableProps.ColumnDefinition<Transaction>[] = [
  {
    id: "id",
    header: "Transaction ID",
    width: 350,
    cell: (item: Transaction) => {
      let url = `?stage=${encodeURIComponent(
        item.stage
      )}&transactionId=${encodeURIComponent(item.id)}`;

      if (item.usecase.includes("StandaloneCost")) {
        url += `&${FLOW_PARAM}=document`;
      }

      return <Link href={url}>{item.id}</Link>;
    },
    sortingField: "id",
  },
  {
    id: "usecase",
    header: "Usecase",
    cell: (item: Transaction) => item.usecase,
    sortingField: "usecase",
    width: 250,
  },
  {
    id: "stage",
    header: "Stage",
    cell: (item: Transaction) => item.stage,
    sortingField: "stage",
  },
  {
    id: "country",
    header: "Country",
    cell: (item: Transaction) => item.country,
    sortingField: "country",
  },
  {
    id: "gl",
    header: "GL",
    cell: (item: Transaction) =>
      `${GL_MAPPING[item.gl] || item.gl} (${item.gl})`,
    sortingField: "gl",
  },
  {
    id: "paymentMethod",
    header: "Payment",
    cell: (item: Transaction) => item.paymentMethod,
    sortingField: "paymentMethod",
    width: 150,
  },
  {
    id: "processedTime",
    header: "Processed Time",
    width: 200,
    cell: (item: Transaction) => {
      const date = new Date(item.processedTime);
      return date.toLocaleString();
    },
    sortingField: "processedTime",
  },
  {
    id: "consumptionType",
    header: "Consumption Type",
    width: 200,
    cell: (item: Transaction) => item.consumptionType,
    sortingField: "consumptionType",
  },
];
export const TABLE_PREFERENCES = {
  pageSize: 15,
  contentDensity: "comfortable" as const,
  stickyHeader: true,
  stickyColumns: { first: 0, last: 0 },
  wrapLines: false,
};

export interface EmptyStateConfig {
  heading: string;
  message: string;
  link?: string;
}

export interface EmptyStateDefinition {
  default: EmptyStateConfig;
  filtered: EmptyStateConfig;
  permission: EmptyStateConfig;
  error: EmptyStateConfig;
}

export const RECENT_TRXN_EMPTY_STATE: EmptyStateDefinition = {
  default: {
    heading: "No transactions",
    message: "No recent transactions to display.",
  },
  filtered: {
    heading: "No matches",
    message: "We can't find any matches for your search.",
  },
  permission: {
    heading: "Permission Error",
    message:
      "You may not have permission to view transactions. Please request access through the permissions portal.",
    link: PERMISSION_LINK,
  },
  error: {
    heading: "Server Error",
    message: "There has been a problem.",
  },
};
export const STAGES = Object.values(Stage).map((stage) => ({
  label: stage,
  value: stage,
}));

export const DEFAULT_OPTION: OptionDefinition = {
  label: "All",
  value: "All",
};

export const GLS = [
  DEFAULT_OPTION,
  ...Object.entries(GL_MAPPING).map(([id, name]) => ({
    label: `${name} (${id})`,
    value: id,
  })),
];

export const PAYMENT_METHOD_OPTIONS: OptionDefinition[] = [
  DEFAULT_OPTION,
  { label: "AMP", value: "AMP" },
  { label: "Apple", value: "Apple" },
  { label: "Google", value: "Google" },
];

export interface FeatureGroup {
  label: string;
  options: OptionDefinition[];
  multiSelect?: boolean;
}

export const GROUPED_FEATURES: FeatureGroup[] = [
  {
    label: "Bundle Types",
    options: [
      { label: "Immediate Bundle", value: "Immediate Bundle" },
      { label: "Season Pass Bundle", value: "Season Pass Bundle" },
      { label: "Preorder Bundle", value: "Preorder Bundle" },
    ],
  },
  {
    label: "Event Source",
    options: [
      { label: "ARK", value: "ARK" },
      { label: "FOIL", value: "FOIL" },
    ],
  },
  {
    label: "Consumption Type",
    options: [
      { label: "Rental", value: "Rental" },
      { label: "Purchase", value: "Purchase" },
      { label: "Subscription", value: "Subscription" },
      { label: "Season Pass", value: "Season Pass" },
      { label: "None", value: "None" },
    ],
  },
  {
    label: "EMP Processor",
    options: [
      { label: "Denarius", value: "Denarius" },
      { label: "Thunderbird", value: "Thunderbird" },
    ],
  },
  {
    label: "Misc",
    multiSelect: true,
    options: [
      { label: "AVOD", value: "AVOD" },
      { label: "MGM", value: "MGM" },
      { label: "Promos", value: "Promos" },
      { label: "Free Trials", value: "Free Trials" },
    ],
  },
];

const FEATURE_GROUP_MAP: Record<string, FeatureGroup> = {};
GROUPED_FEATURES.forEach((group) => {
  group.options.forEach((opt) => {
    FEATURE_GROUP_MAP[opt.value!] = group;
  });
});

export const getFeatureGroup = (value: string): FeatureGroup | undefined =>
  FEATURE_GROUP_MAP[value];

const CLASSIC_MARKETS = ["US", "DE", "JP", "GB"];
const ELCANO_MARKETS = ["FR", "ES", "IN", "CA", "NL", "IT"];
const OTHER_MARKETPLACES = ["AU", "PL", "SE", "BE", "TR", "MX", "BR"];
const ROW_NA_MARKETS = ["AR", "CL", "CO", "CR", "DO", "GT", "PY", "PE"];
const ROW_EU_MARKETS = ["DK", "FI", "NO", "PT", "ZA", "NG", "KE", "EG", "SA", "AE"];
const ROE_EU_MARKETS = ["CZ", "HU", "IS", "IE", "RO", "CH", "LU", "RE", "MQ", "GR", "SK", "HR", "EE", "SI", "RS", "BG", "LT", "UA", "CY", "LV", "MT", "GP", "NC", "PF"];
const ROW_FE_MARKETS = ["ID", "MY", "NZ", "PH", "TW", "TH", "SG"];

export const GROUPED_MARKETPLACES = [
  DEFAULT_OPTION,
  {
    label: "Classic Markets (CMP)",
    options: CLASSIC_MARKETS.map((code) => ({
      label: code,
      value: code,
    })).sort((a, b) => a.label.localeCompare(b.label)),
  },
  {
    label: "Elcano Markets",
    options: ELCANO_MARKETS.map((code) => ({
      label: code,
      value: code,
    })).sort((a, b) => a.label.localeCompare(b.label)),
  },
  {
    label: "ROW NA",
    options: ROW_NA_MARKETS.map((code) => ({
      label: code,
      value: code,
    })).sort((a, b) => a.label.localeCompare(b.label)),
  },
  {
    label: "ROW EU",
    options: ROW_EU_MARKETS.map((code) => ({
      label: code,
      value: code,
    })).sort((a, b) => a.label.localeCompare(b.label)),
  },
  {
    label: "ROW FE",
    options: ROW_FE_MARKETS.map((code) => ({
      label: code,
      value: code,
    })).sort((a, b) => a.label.localeCompare(b.label)),
  },
  {
    label: "ROE",
    options: ROE_EU_MARKETS.map((code) => ({
      label: code,
      value: code,
    })).sort((a, b) => a.label.localeCompare(b.label)),
  },
  {
    label: "Other",
    options: OTHER_MARKETPLACES.map((code) => ({
      label: code,
      value: code,
    })).sort((a, b) => a.label.localeCompare(b.label)),
  },
];
const SALE_USECASES = [
  "OptimisticFulfillment",
  "ChargeSuccess",
  "ChargePending",
  "PrepaidChargeSuccess",
];

const CANCEL_USECASES = [
  "ChargeFail",
  "OrderClosedNonPay",
  "CancelBeforeChargeSuccess",
  "Cancelled",
  "PurchaseChargeBack",
  "CancelChargeBack",
  "FailureAfterSuccess",
  "CancelWithoutRefund",
  "CancelIdeal",
];

const REFUND_USECASES = ["Refunded", "RefundPending", "RefundDeclined"];
const NCP_COST_USECASES = ["StandaloneCost", "StandaloneCostRefund"];

const CONTENT_PARTNER_ROYALTY_USECASES = [
  "StandaloneCostContentPartner",
  "StandaloneCostRefundWithClawbackContentPartner",
  "StandaloneCostCancellation",
  "StandaloneCostTermination",
  "PrepaidBalanceShift",
];

const GOOGLE_ADJUSTMENT_USECASES = ["GoogleAdjustment"];

const FREE_TRIAL_USECASES = ["VetChargeSale", "VetChargeRefund"];

export const GROUPED_USECASES = [
  DEFAULT_OPTION,
  {
    label: "Sale",
    options: SALE_USECASES.map((usecase) => ({
      label: usecase,
      value: usecase,
    })).sort((a, b) => a.label.localeCompare(b.label)),
  },
  {
    label: "Cancel",
    options: CANCEL_USECASES.map((usecase) => ({
      label: usecase,
      value: usecase,
    })).sort((a, b) => a.label.localeCompare(b.label)),
  },
  {
    label: "Refund",
    options: REFUND_USECASES.map((usecase) => ({
      label: usecase,
      value: usecase,
    })).sort((a, b) => a.label.localeCompare(b.label)),
  },
  {
    label: "ContentPartner Cost",
    options: CONTENT_PARTNER_ROYALTY_USECASES.map((usecase) => ({
      label: usecase,
      value: usecase,
    })).sort((a, b) => a.label.localeCompare(b.label)),
  },
  {
    label: "NCP Cost",
    options: NCP_COST_USECASES.map((usecase) => ({
      label: usecase,
      value: usecase,
    })).sort((a, b) => a.label.localeCompare(b.label)),
  },
  {
    label: "Google Adjustment",
    options: GOOGLE_ADJUSTMENT_USECASES.map((usecase) => ({
      label: usecase,
      value: usecase,
    })).sort((a, b) => a.label.localeCompare(b.label)),
  },
  {
    label: "Free Trial",
    options: FREE_TRIAL_USECASES.map((usecase) => ({
      label: usecase,
      value: usecase,
    })).sort((a, b) => a.label.localeCompare(b.label)),
  },
];

export const getModifiedOptions = (
  options: readonly (
    | OptionDefinition
    | { label: string; options: OptionDefinition[] }
  )[],
  selectedOptions: readonly OptionDefinition[]
): (OptionDefinition | { label: string; options: OptionDefinition[] })[] => {
  const hasAllSelected = selectedOptions.some(
    (option) => option.value === DEFAULT_OPTION.value
  );

  return options.map((option) => {
    if ("options" in option) {
      // Handle grouped options
      return {
        ...option,
        options: option.options.map((subOption) => ({
          ...subOption,
          disabled: hasAllSelected,
        })),
      };
    }
    // Handle non-grouped options
    return {
      ...option,
      disabled: hasAllSelected && option.value !== DEFAULT_OPTION.value,
    };
  });
};
